import { logger } from "@/lib/logger";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ScanSearch,
  ChevronDown,
  CheckCircle2,
  RefreshCw,
  TrendingDown,
  ImageOff,
  Layers,
  MousePointerClick,
  Lightbulb,
  ShieldCheck,
  PauseCircle,
  FolderPlus,
} from "lucide-react";
import { FaMeta, FaMicrosoft, FaLinkedin } from "react-icons/fa6";
import { FcGoogle } from "react-icons/fc";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
function authHeaders() {
  const t = localStorage.getItem("roas_session_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
}

const CATEGORY_ICONS = {
  budget_bleeding: TrendingDown,
  creative_fatigue: ImageOff,
  audience_overlap: Layers,
  bidding_inefficiency: MousePointerClick,
};

const SCAN_STEPS = [
  "Scanning for budget bleeding...",
  "Detecting creative fatigue...",
  "Mapping audience overlap...",
  "Benchmarking bid efficiency...",
];

const PlatformIcon = ({ platform, className = "w-3.5 h-3.5" }) => {
  switch (platform) {
    case "meta": return <FaMeta className={cn(className, "text-[#1877F2]")} />;
    case "google": return <FcGoogle className={className} />;
    case "bing": return <FaMicrosoft className={cn(className, "text-[#00A4EF]")} />;
    case "linkedin": return <FaLinkedin className={cn(className, "text-[#0A66C2]")} />;
    default: return <FcGoogle className={className} />;
  }
};

const fmt = (v) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(v || 0);

const fmt2 = (v) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v || 0);

const CountUp = ({ value }) => {
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    let frame;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / 1200, 1);
      setDisplay(value * (1 - Math.pow(1 - p, 3)));
      if (p < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [value]);
  return <>{fmt(display)}</>;
};

export const WastedSpendAudit = ({ onFixApplied }) => {
  const [audit, setAudit] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [scanStep, setScanStep] = useState(0);
  const [days, setDays] = useState("90");
  const [expanded, setExpanded] = useState(null);
  const [fixing, setFixing] = useState(null);

  useEffect(() => {
    const loadLast = async () => {
      try {
        const resp = await fetch(`${API}/audit/wasted-spend`, { credentials: "include", headers: authHeaders() });
        if (resp.ok) {
          const data = await resp.json();
          if (data.success) {
            setAudit(data);
            setDays(String(data.period_days || 90));
          }
        }
      } catch (e) { logger.error("Failed to load saved audit:", e); }
    };
    loadLast();
  }, []);

  const runAudit = async () => {
    setScanning(true);
    setScanStep(0);
    setExpanded(null);
    const stepTimer = setInterval(() => setScanStep((s) => Math.min(s + 1, 3)), 950);
    const started = Date.now();
    try {
      const resp = await fetch(`${API}/audit/wasted-spend`, {
        method: "POST",
        credentials: "include",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ days: parseInt(days, 10) }),
      });
      const data = await resp.json();
      const wait = Math.max(0, 4200 - (Date.now() - started));
      setTimeout(() => {
        clearInterval(stepTimer);
        setScanning(false);
        if (resp.ok && data.success) {
          setAudit(data);
        } else if (data.error === "no_campaigns") {
          toast.error("No campaigns to analyze yet — connect an ad platform and sync first");
        } else {
          toast.error("Audit failed. Please try again.");
        }
      }, wait);
    } catch {
      clearInterval(stepTimer);
      setScanning(false);
      toast.error("Audit failed. Please try again.");
    }
  };

  const pauseCampaign = async (catKey, item) => {
    setFixing(`pause:${item.campaign_id}`);
    try {
      const resp = await fetch(`${API}/audit/fix`, {
        method: "POST",
        credentials: "include",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ action: "pause", campaign_id: item.campaign_id, category_key: catKey }),
      });
      const data = await resp.json();
      if (resp.ok && data.success) {
        setAudit((prev) => ({
          ...prev,
          categories: prev.categories.map((c) =>
            c.key !== catKey ? c : { ...c, items: c.items.map((it) => (it.campaign_id === item.campaign_id ? { ...it, fixed: "paused", recovery: data.recovery } : it)) }
          ),
        }));
        toast.success(`"${item.name}" paused — bleeding stopped`);
        onFixApplied?.("pause");
      } else {
        toast.error(data.detail || "Failed to pause campaign");
      }
    } catch {
      toast.error("Failed to pause campaign");
    }
    setFixing(null);
  };

  const createFixGroup = async (cat) => {
    setFixing(`group:${cat.key}`);
    try {
      const resp = await fetch(`${API}/audit/fix`, {
        method: "POST",
        credentials: "include",
        headers: { ...authHeaders(), "Content-Type": "application/json" },
        body: JSON.stringify({ action: "budget_group", category_key: cat.key }),
      });
      const data = await resp.json();
      if (resp.ok && data.success) {
        setAudit((prev) => ({
          ...prev,
          categories: prev.categories.map((c) =>
            c.key !== cat.key ? c : { ...c, fix_group_id: data.group.group_id, fix_group_name: data.group.name, group_recovery: data.recovery }
          ),
        }));
        toast.success(`Budget Group "${data.group.name}" created with ${data.group.campaign_ids.length} campaigns`);
        onFixApplied?.("group");
      } else {
        toast.error(data.detail || "Failed to create budget group");
      }
    } catch {
      toast.error("Failed to create budget group");
    }
    setFixing(null);
  };

  const recoveryTotals = (audit?.categories || []).reduce(
    (acc, c) => {
      (c.items || []).forEach((it) => {
        if (it.recovery) { acc.recovered += it.recovery.recovered || 0; acc.rate += it.recovery.daily_rate || 0; }
      });
      if (c.group_recovery) { acc.recovered += c.group_recovery.recovered || 0; acc.rate += c.group_recovery.daily_rate || 0; }
      return acc;
    },
    { recovered: 0, rate: 0 }
  );
  const hasRecovery = recoveryTotals.rate > 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid="wasted-spend-audit-card"
      className="bg-[#121212] border border-red-500/20 rounded-xl overflow-hidden"
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-5 border-b border-white/5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center flex-shrink-0">
            <ScanSearch className="w-5 h-5 text-red-400" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-white font-heading font-bold text-lg">Wasted Spend Audit</h2>
              <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 text-[10px]">
                <ShieldCheck className="w-3 h-3 mr-1" /> Read-only
              </Badge>
            </div>
            <p className="text-zinc-400 text-sm">Diagnostic scan of your historical ad spend — nothing in your account is changed</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={days} onValueChange={setDays} disabled={scanning}>
            <SelectTrigger data-testid="audit-days-select" className="w-[130px] bg-black/50 border-white/10 text-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-[#18181B] border-white/10 text-white">
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="60">Last 60 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button
            data-testid="run-audit-btn"
            onClick={runAudit}
            disabled={scanning}
            className="bg-gradient-to-r from-red-600 to-amber-600 hover:from-red-700 hover:to-amber-700 text-white font-semibold rounded-lg"
          >
            {scanning ? (
              <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Scanning...</>
            ) : audit ? (
              <><RefreshCw className="w-4 h-4 mr-2" /> Re-run Audit</>
            ) : (
              <><ScanSearch className="w-4 h-4 mr-2" /> Run Audit</>
            )}
          </Button>
        </div>
      </div>

      <AnimatePresence mode="wait">
        {scanning ? (
          <motion.div
            key="scanning"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="p-6"
            data-testid="audit-scanning"
          >
            <div className="max-w-md mx-auto space-y-2.5">
              {SCAN_STEPS.map((label, i) => (
                <div
                  key={label}
                  className={cn(
                    "flex items-center gap-3 rounded-lg px-4 py-2.5 text-sm transition-colors",
                    i === scanStep ? "bg-red-500/10 border border-red-500/30 text-white" :
                    i < scanStep ? "bg-white/[0.03] text-zinc-400" : "text-zinc-700"
                  )}
                >
                  {i < scanStep ? (
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                  ) : (
                    <span className={cn("w-4 h-4 rounded-full border-2", i === scanStep ? "border-red-400 border-t-transparent animate-spin" : "border-zinc-800")} />
                  )}
                  {label}
                </div>
              ))}
              <div className="h-1.5 rounded-full bg-white/5 overflow-hidden mt-4">
                <motion.div
                  className="h-full bg-gradient-to-r from-red-500 to-amber-500 rounded-full"
                  animate={{ width: `${((scanStep + 1) / 4) * 95}%` }}
                  transition={{ duration: 0.8 }}
                />
              </div>
            </div>
          </motion.div>
        ) : audit ? (
          <motion.div key="results" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="p-5 space-y-5" data-testid="audit-results">
            {/* Headline */}
            <div className="flex flex-col md:flex-row md:items-center gap-4">
              <div className="flex-1 bg-gradient-to-br from-red-950/40 to-[#121212] border border-red-500/30 rounded-xl p-5">
                <p className="text-red-300 text-xs font-bold tracking-widest uppercase mb-1">Potential Wasted Spend</p>
                <p data-testid="audit-total-waste" className="text-4xl font-bold text-red-400 tabular-nums">
                  <CountUp value={audit.total_waste} />
                </p>
                <p className="text-zinc-400 text-sm mt-1">in the last {audit.period_days} days</p>
              </div>
              <div className={cn("grid gap-3 flex-1", hasRecovery ? "grid-cols-2 sm:grid-cols-4" : "grid-cols-3")}>
                <div className="bg-white/[0.03] border border-white/5 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-amber-400">{audit.waste_pct}%</p>
                  <p className="text-zinc-400 text-xs mt-1">of spend wasted</p>
                </div>
                <div className="bg-white/[0.03] border border-white/5 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-white">{fmt(audit.total_spend_analyzed)}</p>
                  <p className="text-zinc-400 text-xs mt-1">spend analyzed</p>
                </div>
                <div className="bg-white/[0.03] border border-white/5 rounded-xl p-4 text-center">
                  <p className="text-2xl font-bold text-white">{audit.campaigns_analyzed}</p>
                  <p className="text-zinc-400 text-xs mt-1">campaigns scanned</p>
                </div>
                {hasRecovery && (
                  <div data-testid="audit-total-recovered" className="bg-emerald-500/5 border border-emerald-500/20 rounded-xl p-4 text-center">
                    <p className="text-2xl font-bold text-emerald-400 tabular-nums">{fmt2(recoveryTotals.recovered)}</p>
                    <p className="text-zinc-400 text-xs mt-1">recovered · +{fmt2(recoveryTotals.rate)}/day</p>
                  </div>
                )}
              </div>
            </div>

            {/* Categories */}
            <div className="space-y-2">
              {audit.categories.map((cat) => {
                const Icon = CATEGORY_ICONS[cat.key] || TrendingDown;
                const isOpen = expanded === cat.key;
                return (
                  <div key={cat.key} data-testid={`audit-category-${cat.key}`} className="border border-white/5 rounded-xl bg-white/[0.02]">
                    <button
                      data-testid={`audit-category-toggle-${cat.key}`}
                      onClick={() => setExpanded(isOpen ? null : cat.key)}
                      className="w-full flex items-center justify-between p-4 text-left"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-red-500/10 flex items-center justify-center flex-shrink-0">
                          <Icon className="w-4.5 h-4.5 text-red-400" />
                        </div>
                        <div>
                          <p className="text-white font-semibold text-sm">{cat.title}</p>
                          <p className="text-zinc-400 text-xs">{cat.description}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="text-red-400 font-bold tabular-nums">{fmt(cat.wasted_amount)}</p>
                          <p className="text-zinc-500 text-xs">{cat.affected_count} finding{cat.affected_count === 1 ? "" : "s"}</p>
                        </div>
                        <ChevronDown className={cn("w-4 h-4 text-zinc-400 transition-transform", isOpen && "rotate-180")} />
                      </div>
                    </button>
                    <AnimatePresence>
                      {isOpen && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="overflow-hidden"
                        >
                          <div className="px-4 pb-4 space-y-2">
                            {cat.items.length === 0 ? (
                              <p className="text-zinc-500 text-sm flex items-center gap-2">
                                <CheckCircle2 className="w-4 h-4 text-emerald-400" /> No issues found in this category
                              </p>
                            ) : (
                              cat.items.map((item, i) => (
                                <div key={`${item.campaign_id}-${i}`} className="flex items-center justify-between bg-black/30 rounded-lg px-3 py-2.5">
                                  <div className="flex items-center gap-2.5 min-w-0">
                                    <PlatformIcon platform={item.platform} />
                                    <div className="min-w-0">
                                      <p className="text-zinc-200 text-sm truncate">{item.name}</p>
                                      <p className="text-zinc-400 text-xs truncate">{item.detail}</p>
                                    </div>
                                  </div>
                                  <div className="flex flex-col items-end gap-1 flex-shrink-0 ml-3">
                                    <div className="flex items-center gap-2.5">
                                      <p className="text-red-400 font-semibold text-sm tabular-nums">{fmt(item.amount)}</p>
                                      {cat.key === "budget_bleeding" && item.campaign_id && (
                                        item.fixed ? (
                                          <Badge data-testid={`audit-fixed-badge-${item.campaign_id}`} className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 text-[10px]">
                                            <CheckCircle2 className="w-3 h-3 mr-1" /> Paused
                                          </Badge>
                                        ) : (
                                          <Button
                                            data-testid={`audit-fix-pause-${item.campaign_id}`}
                                            size="sm"
                                            variant="outline"
                                            disabled={fixing === `pause:${item.campaign_id}`}
                                            onClick={() => pauseCampaign(cat.key, item)}
                                            className="h-7 px-2.5 text-xs border-red-500/30 text-red-300 hover:bg-red-500/10 hover:text-red-200"
                                          >
                                            {fixing === `pause:${item.campaign_id}` ? (
                                              <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                                            ) : (
                                              <PauseCircle className="w-3 h-3 mr-1" />
                                            )}
                                            Fix: Pause
                                          </Button>
                                        )
                                      )}
                                    </div>
                                    {item.recovery && (
                                      <p data-testid={`audit-recovery-${item.campaign_id}`} className="text-emerald-400 text-[11px] font-medium tabular-nums">
                                        +{fmt2(item.recovery.recovered)} recovered · {fmt2(item.recovery.daily_rate)}/day
                                      </p>
                                    )}
                                  </div>
                                </div>
                              ))
                            )}
                            {cat.items.length > 0 && (
                              <div className="flex items-start gap-2 bg-amber-500/5 border border-amber-500/20 rounded-lg px-3 py-2.5">
                                <Lightbulb className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                                <div className="flex-1">
                                  <p className="text-amber-200/80 text-xs">{cat.recommendation}</p>
                                  {cat.key !== "budget_bleeding" && (
                                    cat.fix_group_id ? (
                                      <div className="mt-2 space-y-1">
                                        <p data-testid={`audit-fix-group-done-${cat.key}`} className="text-emerald-400 text-xs flex items-center gap-1.5">
                                          <CheckCircle2 className="w-3.5 h-3.5" />
                                          Budget Group "{cat.fix_group_name}" created — AI optimization will rebalance these campaigns
                                        </p>
                                        {cat.group_recovery && (
                                          <p data-testid={`audit-group-recovery-${cat.key}`} className="text-emerald-400 text-[11px] font-medium tabular-nums pl-5">
                                            +{fmt2(cat.group_recovery.recovered)} recovered so far · {fmt2(cat.group_recovery.daily_rate)}/day
                                          </p>
                                        )}
                                      </div>
                                    ) : (
                                      <Button
                                        data-testid={`audit-fix-group-${cat.key}`}
                                        size="sm"
                                        disabled={fixing === `group:${cat.key}`}
                                        onClick={() => createFixGroup(cat)}
                                        className="mt-2 h-7 px-3 text-xs bg-gradient-to-r from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 text-white font-semibold"
                                      >
                                        {fixing === `group:${cat.key}` ? (
                                          <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
                                        ) : (
                                          <FolderPlus className="w-3 h-3 mr-1" />
                                        )}
                                        Fix this: Create Budget Group
                                      </Button>
                                    )
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>

            <p className="text-zinc-500 text-xs flex items-center gap-2">
              <ShieldCheck className="w-3.5 h-3.5" />
              Diagnostic estimate based on your synced campaign data · Read-only, no changes made
              {audit.created_at && <span>· Last run {new Date(audit.created_at).toLocaleString()}</span>}
            </p>
          </motion.div>
        ) : (
          <motion.div key="empty" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="p-6 text-center" data-testid="audit-empty-state">
            <p className="text-white font-semibold mb-1">How much of your ad spend is being wasted?</p>
            <p className="text-zinc-400 text-sm mb-4 max-w-lg mx-auto">
              Run a free read-only scan of your last 30–90 days to uncover budget bleeding, creative fatigue,
              audience overlap and bidding inefficiency — with a dollar figure on each.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-2">
              {["Budget Bleeding", "Creative Fatigue", "Audience Overlap", "Bidding Inefficiency"].map((c) => (
                <span key={c} className="text-xs text-zinc-400 bg-white/5 border border-white/10 rounded-full px-3 py-1">{c}</span>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};
